/**
 * Gespraech mit dem Sync-Speicher unter `/api/sync/*` (T-04) - dieselbe
 * Herkunft wie die App, hinter demselben Cloudflare-Access-Login (T-03).
 * Gesendet wird nur Geheimtext (siehe `krypto.ts`).
 */
export interface SchluesselInfo { kennung: string; salz: string; runden: number; pruefwert: string }
export interface ServerZeile { t: string; id: string; seq: number; blob: string }
export interface SendeZeile { t: string; id: string; basis: number; blob: string }

export type FehlerArt = 'offline' | 'login' | 'kennung' | 'nicht_verfuegbar' | 'server' | 'version' | 'passwort';

export class SyncFehler extends Error {
  constructor(readonly art: FehlerArt, message: string) {
    super(message);
    this.name = 'SyncFehler';
  }
}

/** Austauschbar fuer Tests: dort antwortet der Server-Code direkt. */
export type Transport = (pfad: string, init: RequestInit) => Promise<Response>;

export const fetchTransport: Transport = (pfad, init) =>
  fetch(`/api/sync/${pfad}`, { ...init, credentials: 'same-origin', redirect: 'manual', cache: 'no-store' });

export class SyncApi {
  constructor(private readonly transport: Transport = fetchTransport, public kennung = '') {}

  private async rufe(pfad: string, init: RequestInit = {}): Promise<Response> {
    let r: Response;
    try {
      r = await this.transport(pfad, { ...init, headers: { ...(this.kennung ? { 'X-Sync-Kennung': this.kennung } : {}), ...init.headers } });
    } catch {
      throw new SyncFehler('offline', 'Keine Verbindung');
    }
    // Abgelaufener Access-Login leitet zur Anmeldeseite um.
    if (r.type === 'opaqueredirect' || r.status === 401 || r.status === 403 || (r.status >= 300 && r.status < 400)) {
      throw new SyncFehler('login', 'Anmeldung abgelaufen - Seite neu laden');
    }
    if (r.status === 409 && pfad !== 'schluessel') throw new SyncFehler('kennung', 'Die Server-Kopie wurde neu angelegt');
    return r;
  }

  private async json<T>(r: Response): Promise<T> {
    if (!(r.headers.get('content-type') ?? '').includes('json')) throw new SyncFehler('nicht_verfuegbar', 'Kein Sync-Dienst erreichbar');
    if (!r.ok) {
      const f = (await r.json().catch(() => ({}))) as { fehler?: string };
      throw new SyncFehler('server', f.fehler ?? `Sync-Dienst: ${r.status}`);
    }
    return (await r.json()) as T;
  }

  /** `null` = Server erreichbar, aber noch nicht eingerichtet. */
  async schluesselHolen(): Promise<SchluesselInfo | null> {
    const r = await this.rufe('schluessel');
    if (r.status === 404 && (r.headers.get('content-type') ?? '').includes('json')) return null;
    return this.json<SchluesselInfo>(r);
  }

  /** `null` = ein anderes Geraet war schneller. */
  async schluesselAnlegen(salz: string, runden: number, pruefwert: string): Promise<string | null> {
    const r = await this.rufe('schluessel', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ salz, runden, pruefwert }) });
    if (r.status === 409) return null;
    return (await this.json<{ kennung: string }>(r)).kennung;
  }

  async allesLoeschen(): Promise<void> {
    const r = await this.rufe('alles', { method: 'DELETE' });
    if (!r.ok) throw new SyncFehler('server', `Sync-Dienst: ${r.status}`);
  }

  aenderungen(seit: number): Promise<{ zeilen: ServerZeile[]; mehr: boolean }> {
    return this.rufe(`aenderungen?seit=${seit}`).then((r) => this.json(r));
  }

  senden(zeilen: SendeZeile[]): Promise<{ ok: { t: string; id: string; seq: number }[]; abgelehnt: ServerZeile[] }> {
    return this.rufe('aenderungen', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ zeilen }) }).then((r) => this.json(r));
  }

  async anhangHoch(id: string, daten: Uint8Array<ArrayBuffer>): Promise<void> {
    const r = await this.rufe(`anhang/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'content-type': 'application/octet-stream' }, body: daten });
    if (!r.ok) await this.json(r);
  }

  async anhangHolen(id: string): Promise<Uint8Array<ArrayBuffer> | null> {
    const r = await this.rufe(`anhang/${encodeURIComponent(id)}`);
    if (r.status === 404) return null;
    if (!r.ok) await this.json(r);
    return new Uint8Array(await r.arrayBuffer());
  }

  async anhangLoeschen(id: string): Promise<void> {
    await this.rufe(`anhang/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
